import { normalize } from 'normalizr';
import cookies from '@utils/cookies';
import { authTypes, analytics } from '../constants';
import { fetchNewToken, authenticate, fetchMyInfo } from '../actions/auth';

const host = process.env.BACKEND_URL;
const cmsUrl = process.env.CMS_URL;
const abServer = 'https://ab.unacademy.com/';

let isRefreshing = false;
let subscribers = [];

const checkStatus = (response) => {
  if (response.ok) return response;
  const error = new Error(response.statusText);
  error.response = response;
  const contentType = response.headers.get('content-type');
  if (contentType.includes('text/plain') || contentType.includes('text/html')) {
    error.status = 500;
  }
  return Promise.reject(error);
};

const callAPIMiddleware = ({ dispatch, getState }) => (next) => (action) => {
  if (typeof action === 'function') {
    return action(dispatch, getState);
  }

  if (action.type === analytics.ANALYTICS && action.entityName) {
    return next(action);
  }

  if (!action || !action.endpoint || !action.method) {
    return next(action);
  }
  const {
    endpoint,
    type,
    method,
    data,
    params,
    schema,
    entityType,
    directPaginatedEntity,
    processData,
    isCMS,
    isABserver,
    isFormData,
    callback,
    headers: overrideHeaders,
    ...rest
  } = action;
  let url = '';
  try {
    // eslint-disable-next-line no-new
    const absoluteUrl = new URL(endpoint);
    url = absoluteUrl.href;
  } catch (e) {
    url = `${host}${endpoint}`;
    if (isCMS) {
      url = `${cmsUrl}${endpoint}`;
    } else if (isABserver) {
      url = `${abServer}${endpoint}`;
    }
  }
  let queryString = '';

  if (params) queryString = encodeURIComponent(JSON.stringify(params));
  if (queryString) url = `${url}?${queryString}`;

  const accessToken = cookies.readCookie('accessToken', getState);
  const refreshToken = cookies.readCookie('refreshToken', getState);

  next({ ...rest, type: `${type}_REQUEST` });

  const headers = {
    ...(!isCMS && {
      authorization: `Bearer ${accessToken}`
    }),
    ...(!isFormData && {
      'Content-Type': 'application/json'
    }),
    ...(overrideHeaders || {})
  };
  if (url.includes(host)) {
    const platform = getState().helper.isMobile ? 7 : 0;
    headers['X-Platform'] = platform;
  }
  const actionPromise = fetch(url, {
    method,
    body: isFormData ? data : JSON.stringify(data),
    headers,
    credentials: 'same-origin' // include,
  });

  return actionPromise
    .then(checkStatus)
    .then(async (response) => {
      if (response.status === 204) return response;
      const resp = await response.json();
      return resp;
    })
    .then((result) => {
      if (callback) callback(result);
      if (!schema)
        return next({
          ...rest,
          result,
          type: `${type}_SUCCESS`
        });
      let formattedResults = result;

      // result preprocessing before normalize
      if (processData) {
        formattedResults = processData(formattedResults);
      }
      if (entityType) {
        if (directPaginatedEntity) {
          formattedResults = {
            ...formattedResults,
            results: formattedResults.results.map((item) => ({
              [entityType]: item
            }))
          };
        } else if (Array.isArray(formattedResults)) {
          formattedResults = formattedResults.map((item) => ({
            [entityType]: item
          }));
        } else {
          formattedResults = {
            [entityType]: formattedResults
          };
        }
      }
      const normalizedData = normalize(formattedResults, schema);
      next({ ...rest, entities: normalizedData.entities });
      return next({
        ...rest,
        result: normalizedData.result,
        type: `${type}_SUCCESS`,
        rawResponse: result
      });
    })
    .catch(async (error) => {
      if (error?.response?.status === 401) {
        if (accessToken && refreshToken && !isRefreshing) {
          if (action.type !== 'ME') subscribers.push(action);
          isRefreshing = true;

          try {
            const response = await (await fetchNewToken(refreshToken)).json();
            isRefreshing = false;
            dispatch(
              authenticate({
                accessToken: response.access_token,
                refreshToken: response.refresh_token
              })
            );
            await dispatch(fetchMyInfo());
            subscribers.forEach((cb) => dispatch(cb));
          } catch (err) {
            return next({
              ...rest,
              err,
              show: true,
              loginType: 'login',
              type: authTypes.LOGIN_MODAL
            });
          } finally {
            subscribers = [];
          }
        } else {
          return next({
            ...rest,
            show: true,
            loginType: 'login',
            type: authTypes.LOGIN_MODAL
          });
        }
      }
      if (error?.response?.status >= 500 || error.status >= 500) {
        return next({ ...rest, error, type: `${type}_FAILURE` });
      }
      const errorResp = await error?.response?.json();
      return next({
        ...rest,
        error: errorResp,
        type: `${type}_FAILURE`,
        statusCode: error?.response?.status
      });
    });
};

export default callAPIMiddleware;
